import { Box, Typography, useTheme, CircularProgress } from "@mui/material";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { tokens } from "../../../../../../theme";
import Text from "../../../../../../components/theme/text/Text";
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import { useTranslation } from "react-i18next";
import Subheading from "../../../../../../components/theme/text/Subheading";
import { useGetSettingsQuery, useGetSettingsForRelationQuery } from "../../../../../../newapi/global/generalApiSlice";
import Shbutton from "../../../../../../components/theme/buttons/Shbutton";
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { useFullImportMutation, useSingleImportMutation, useSaveAdjustmentChargeOptionsMutation } from "../../../../../../newapi/global/uploadApiSlice";
import { showToastMessage, showToastMessageRequestError } from "../../../../../../utils/toasts";
import SHQuickAction from "../../../../../../components/theme/Modal/SHQuickAction";
import MDynamicValues from "../../../../../../components/global/Modals/MDynamicValues";
import { shippingCostShipperOptions, adjustmentChargeShipperOptions, adjustmentChargeReturnShippers } from "../../../../../../utils/importColumnDefinitions";
import { useGetInvoiceProductsSelectQuery } from "../../../../../../newapi/financial/invoiceProductSlice";
import { AdjustmentChargeSkipList, AdjustmentChargeProductMapping, buildAdjustmentChargeOptions } from "../../../../../../components/global/shared/AdjustmentChargeImportOptions";

const SetupPanelImport = ( props ) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [uploadFile] = useFullImportMutation();
    const [uploadSingleFile] = useSingleImportMutation();
    const [selectedFile, setSelectedFile] = useState(null);
    const [source, setSource] = useState(props.source ? props.source : "monta");
    const [uploadType, setUploadType] = useState(props.type ?? 'warehouselocations');
    const fileInputRef = useRef(null);
    const singleFileInputRef = useRef(null);
    const [uploadResults, setUploadResults] = useState(null);
    const [uploadedFileName, setUploadedFileName] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [productImportModalOpen, setProductImportModalOpen] = useState(false);
    const [pendingProductFile, setPendingProductFile] = useState(null);
    const [shippingCostsModalOpen, setShippingCostsModalOpen] = useState(false);
    const [adjustmentChargesModalOpen, setAdjustmentChargesModalOpen] = useState(false);
    // The naheffingen import options are kept per shipper, so the shipper picked in the
    // modal decides which stored options the other fields start from.
    const [adjustmentChargesShipper, setAdjustmentChargesShipper] = useState("stockhub");
    const { data: adjustmentChargeSettings } = useGetSettingsQuery("adjustmentcharges");
    // A naheffing can land on a naheffing product or on a surcharge product of the relation.
    const { data: invoiceProducts } = useGetInvoiceProductsSelectQuery("adjustment_charges,surcharges");
    const [saveAdjustmentChargeOptions] = useSaveAdjustmentChargeOptionsMutation();

    const [view, setView] = useState({
        title: 'return_portal',
        icon: <AssignmentReturnIcon />,
    });

    // Function to trigger file input click
    const handleClick = (uploadType) => {
        if (fileInputRef.current) {
            // Store the upload type for use in handleFileChange
            fileInputRef.current.dataset.uploadType = uploadType;
            fileInputRef.current.click();
        }
    };

    // Function to trigger single file input click
    const handleSingleClick = (importType) => {
        // If it's products, show the modal first
        if (importType === "producten") {
            setProductImportModalOpen(true);
            return;
        }

        // Shipping costs need the shipper first, its invoice layout decides the columns
        if (importType === "verzendkosten") {
            setShippingCostsModalOpen(true);
            return;
        }

        // Naheffingen too, and the skip list and product mapping kept for that shipper
        if (importType === "naheffingen") {
            setAdjustmentChargesModalOpen(true);
            return;
        }

        if (singleFileInputRef.current) {
            // Store the import type for use in handleSingleFileChange
            singleFileInputRef.current.dataset.importType = importType;
            singleFileInputRef.current.click();
        }
    };

    // Handle file selection and auto-upload
    const handleFileChange = async (event) => {
        if (event.target.files && event.target.files[0]) {
            const file = event.target.files[0];
            const uploadType = event.target.dataset.uploadType || "stockhub";
            setSelectedFile(file);
            setIsUploading(true);
            setUploadResults(null);

            // Auto-upload the file
            try {
                const response = await uploadFile({
                    source: source,
                    type: uploadType,
                    file: file
                }).unwrap();

                if (response) {
                    // Store the upload results and filename
                    setUploadResults(response);
                    setUploadedFileName(file.name);
                    props.setData({
                        source: source,
                        columns: response.columns,
                        headers: response.headers,
                        internalFile: response.file,
                        uploadType: uploadType,
                        file: file.name
                    });
                }
            } catch (error) {
                console.error('Error uploading file:', error);
            } finally {
                setIsUploading(false);
                // Reset selectedFile so user can upload another file
                setSelectedFile(null);
                // Reset file input
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
            }
        }
    };

    // Handle single file selection and auto-upload
    const handleSingleFileChange = async (event) => {
        if (event.target.files && event.target.files[0]) {
            const file = event.target.files[0];
            const importType = event.target.dataset.importType || "klanten";

            // Parse query params if they exist
            let queryParams = {};
            if (event.target.dataset.queryParams) {
                try {
                    queryParams = JSON.parse(event.target.dataset.queryParams);
                } catch (e) {
                    console.error('Error parsing query params:', e);
                }
            }

            setSelectedFile(file);
            setIsUploading(true);
            setUploadResults(null);

            // Auto-upload the file
            try {
                const response = await uploadSingleFile({
                    type: importType,
                    file: file,
                    ...queryParams
                }).unwrap();

                if (response) {
                    // Store the upload results and filename
                    setUploadResults(response);
                    setUploadedFileName(file.name);
                }
            } catch (error) {
                console.error('Error uploading single file:', error);
            } finally {
                setIsUploading(false);
                // Reset selectedFile so user can upload another file
                setSelectedFile(null);
                // Reset file input
                if (singleFileInputRef.current) {
                    singleFileInputRef.current.value = '';
                    // Clear stored query params
                    delete singleFileInputRef.current.dataset.queryParams;
                }
            }
        }
    };

    // Handle product import confirmation from modal
    const handleProductImportConfirm = (values) => {
        if (singleFileInputRef.current) {
            // Store the import type for use in handleSingleFileChange
            singleFileInputRef.current.dataset.importType = "producten";
            // Store query params for later use
            singleFileInputRef.current.dataset.queryParams = JSON.stringify(values);
            singleFileInputRef.current.click();
        }
    };

    // Handle shipping costs confirmation from modal
    const handleShippingCostsImportConfirm = (values) => {
        if (singleFileInputRef.current) {
            // Store the import type for use in handleSingleFileChange
            singleFileInputRef.current.dataset.importType = "verzendkosten";
            // Store query params for later use
            singleFileInputRef.current.dataset.queryParams = JSON.stringify(values);
            singleFileInputRef.current.click();
        }
    };

    // Handle naheffingen confirmation from modal. The options go along with the file and
    // are stored by the import, so the next import of this shipper starts from them.
    const handleAdjustmentChargesImportConfirm = (values) => {
        if (singleFileInputRef.current) {
            const options = buildAdjustmentChargeOptions(values);

            singleFileInputRef.current.dataset.importType = "naheffingen";
            singleFileInputRef.current.dataset.queryParams = JSON.stringify({ shipperType: values.shipperType, options });
            singleFileInputRef.current.click();
        }
    };

    // Stores the options without importing, so a configuration survives closing the modal.
    const handleAdjustmentChargesOptionsSave = async (values) => {
        try {
            await saveAdjustmentChargeOptions({ shipperType: values.shipperType, options: buildAdjustmentChargeOptions(values) }).unwrap();
            showToastMessage("success", t("update_successfull"));
        } catch (error) {
            showToastMessageRequestError(t("update_failed"), t, error);
        }
    };

    // Rebuilt when the shipper changes, so the skip list and product mapping start from
    // what is stored for that shipper. The modal reinitialises its values on new fields.
    const adjustmentChargeFields = useMemo(() => {
        const stored = adjustmentChargeSettings?.find(setting => setting.key === `adjustmentcharges_import_${adjustmentChargesShipper}`)?.objectBody ?? {};

        return [
            {
                name: "shipperType",
                label: t("shipper"),
                type: "list",
                required: true,
                defaultValue: adjustmentChargesShipper,
                options: adjustmentChargeShipperOptions,
                onChange: (shipperType) => setAdjustmentChargesShipper(shipperType)
            },
            ...(adjustmentChargeReturnShippers.includes(adjustmentChargesShipper) ? [{
                name: "includeReturns",
                label: t("adjustment_charges_include_returns"),
                explainerTitle: t("adjustment_charges_include_returns"),
                explainerSubTitle: t("adjustment_charges_include_returns_explainer"),
                type: "boolean",
                defaultValue: stored.includeReturns === true
            }] : []),
            {
                name: "skip",
                label: t("adjustment_charges_skip"),
                explainerTitle: t("adjustment_charges_skip"),
                explainerSubTitle: t("adjustment_charges_skip_explainer"),
                type: "custom",
                alignTop: true,
                defaultValue: stored.skip ?? [],
                hidden: (values) => !values.shipperType,
                render: ({ value, onChange }) => <AdjustmentChargeSkipList value={value} onChange={onChange} />
            },
            {
                name: "products",
                label: t("adjustment_charges_products"),
                explainerTitle: t("adjustment_charges_products"),
                explainerSubTitle: t("adjustment_charges_products_explainer"),
                type: "custom",
                alignTop: true,
                defaultValue: stored.products ?? [],
                hidden: (values) => !values.shipperType,
                render: ({ value, onChange }) => <AdjustmentChargeProductMapping value={value} onChange={onChange} invoiceProducts={invoiceProducts} />
            }
        ];
    }, [adjustmentChargeSettings, adjustmentChargesShipper, invoiceProducts, t]);


  useEffect(() => {
    props.replaceView({
        ...props.config,
    },);

    return () => {};
  }, []);

  return (
    <Box
      sx={{
        height: 1,
        "@media screen and (max-width: 47.9375em)": {
          paddingTop: 0,
          paddingX: 0,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: 1,
          gap: 2,
          overflow: "hidden",
          background:
            theme.palette.mode === "dark"
              ? colors.grey[300]
              : colors.bg["tertiary"],
          paddingX: 2,
          paddingTop: 4,
          height: 1,
          position: "relative",
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          "@media screen and (max-width: 47.9375em)": {
            paddingTop: 0,
            paddingX: 0,
            background: colors.bg["tertiary"],
          },
          "& .carrier": {
            paddingLeft: 0,
            marginRight: 2,
          },
          "& [aria-label='Carrier']": {
            paddingLeft: 0,
            marginRight: 2,
          },
        }}
      >
        <Box
          sx={{
            paddingX: 3,
            "@media screen and (max-width: 47.9375em)": {
              display: "none",
            },
          }}
        >
          <Subheading>{t("core_general_title")}</Subheading>
          {/* <Text light>{t("core_general_subtitle")}</Text> */}
        </Box>
        
        <Box
          sx={{
            background:
              theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[0],
            flex: 1,
            display:"flex",
            flexDirection:"column",
            gap:2,
            pt:3,
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            "@media screen and (max-width: 48em)": {
              background:
                theme.palette.mode === "dark"
                  ? colors.grey[200]
                  : colors.grey[0],
            },
          }}
        >
          <Box
            sx={{
              background:
                theme.palette.mode === "dark"
                  ? colors.grey[400]
                  : colors.grey[0],
              px: 3,
              display:"flex",
              flexDirection:"column",
              gap:1,
            }}
          >
            {/* Hidden file input */}
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleFileChange}
              accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
            />

            {/* Hidden file input for single imports */}
            <input
              type="file"
              ref={singleFileInputRef}
              style={{ display: 'none' }}
              onChange={handleSingleFileChange}
              accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
            />

            {isUploading && (
            <Box
                sx={{
                background: colors["blue"][100],
                color: colors["blue"][400],
                padding: 2,
                width: 1,
                marginBottom: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2
                }}
            >
                <CircularProgress size={24} />
                <Typography fontWeight={"bold"} fontSize={17}>
                {t("upload_processing")}: {selectedFile ? selectedFile.name : ""}
                </Typography>
            </Box>
            )}

            {!isUploading && uploadResults && (
                <Box
                    sx={{
                    background: uploadResults.results.some(x => x.notImported.length > 0) ? colors["red"][100] : colors["blue"][100],
                    color: uploadResults.results.some(x => x.notImported.length > 0) ? colors["red"][400] : colors["blue"][400],
                    padding: 2,
                    width: 1,
                    marginBottom: 2
                    }}
                >
                    <Typography fontWeight={"bold"} fontSize={17}>
                        {t("upload_file_results_title")}: {uploadedFileName || ""}
                    </Typography>

                    <Box sx={{ paddingTop: 0.5, maxWidth: 500 }}>
                    {/* Errors */}
                    {uploadResults.errors?.length > 0 && (
                        <>
                        <Typography fontSize={14} fontWeight="bold" color="error">
                            Errors:
                        </Typography>
                        {uploadResults.errors.map((err, idx) => (
                            <Typography key={idx} fontSize={14}>
                            • {err.error}
                            </Typography>
                        ))}
                        </>
                    )}

                    {/* Results */}
                    {uploadResults.results?.length > 0 && (
                        <>
                        <Typography fontSize={14} fontWeight="bold" sx={{ mt: 1 }}>
                            {t("import_results")}:
                        </Typography>
                        {uploadResults.results.map((res, idx) => (
                            <Box key={idx} sx={{ ml: 2, mt: 0.5 }}>
                            <Typography fontSize={14}>{t("import_sheet")}: {res.sheet}</Typography>
                            {res.created !== undefined && <Typography fontSize={14}>{t("import_created")}: {res.created}</Typography>}
                            {res.found !== undefined && <Typography fontSize={14}>{t("import_existing")}: {res.found}</Typography>}
                            {res.updated !== undefined && <Typography fontSize={14}>{t("import_updated")}: {res.updated}</Typography>}
                            {res.skipped !== undefined && <Typography fontSize={14}>{t("import_skipped")}: {res.skipped}</Typography>}
                            {res.deactivated !== undefined && <Typography fontSize={14}>{t("import_deactivated")}: {res.deactivated}</Typography>}

                            {res.notImported?.length > 0 && (
                                <Box sx={{ ml: 2 }}>
                                <Typography fontSize={14} fontWeight="bold">
                                    {t("import_row_errors")}:
                                </Typography>
                                {res.notImported.map((ni, niIdx) => (
                                    <Typography key={niIdx} fontSize={14}>
                                    {t("line")} {ni.line}: {ni.name} — {ni.error}
                                    </Typography>
                                ))}
                                </Box>
                            )}
                            </Box>
                        ))}
                        </>
                    )}
                    </Box>
                </Box>
            )}

            <Box
            sx={{
                width: 1,
            }}
            >
            <Box
                sx={{
                width: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 4,
                marginTop: 1,
                marginBottom: 1,
                cursor: 'pointer',
                "@media screen and (max-width: 47.9375em)": {
                    display:"none"
                },
                }}
            >
                <Text
                    sx={props.explainer ? {display: "flex", alignItems: "center"} : {}} 
                    noBreak 
                    fontSize={16} bold >
                    {t("combined_imports")}
                </Text>
            </Box>
            <Box
                sx={{
                display:"none",
                "@media screen and (max-width: 47.9375em)": {
                    display:"block"
                },
                }}
            >
                <Box
                sx={{
                    ml:"-12px",
                    mr:"-12px",
                    pl:2,
                    pr:2,
                    display:"flex",
                    borderRadius:7,
                    gap:2,
                    alignItems:"center",
                    justifyContent:"space-between",
                    background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                    border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                    boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                }}
                >
                <Text
                    sx={props.explainer ? {display: "flex", alignItems: "center"} : {}} 
                    noBreak 
                    size={"fs13"} >
                    {selectedFile ? `Stockhub import: ${selectedFile.name}` : "Stockhub import"}
                </Text>
                </Box>
                <Text 
                    light
                    sx={{
                    pt:1,
                    }}
                >
                    {t("upload_stockhub_file")}
                </Text>
                <Shbutton onClick={() => handleClick("stockhub")} color="primary" variant="text" startIcon={<FileUploadIcon/>} disabled={isUploading}>
                    {selectedFile ? t("uploaded") : t("upload")}
                </Shbutton>
            </Box>
            <Box
                sx={{
                width: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 4,
                cursor: 'pointer',
                "@media screen and (max-width: 47.9375em)": {
                    display:"none"
                },
                }}
            >
                <SHQuickAction
                    component={
                        <Box
                            sx={{
                                px:1.5,
                                display:"flex",
                                flexDirection:"column"
                            }}
                        >
                            <Text bold>{t("requirements_import_file")}</Text>
                            <Box sx={{ mt: 1 }}>
                                <Text size="fs12" bold>{t("sheets_overview")}</Text>
                                <Box sx={{ mt: 0.5, ml: 1 }}>
                                    <Text size="fs12" bold>{t("sheet_name")}:&nbsp;</Text>
                                    <Text size="fs12" light>klanten</Text>
                                </Box>
                                <Box sx={{ mt: 0.5, ml: 1 }}>
                                    <Text size="fs11" bold>{t("headers")}:&nbsp;</Text>
                                    <Text size="fs11" light>Klant*, Naam contactpersoon*, Straat, Huisnummer, Toevoeging, Postcode, Plaats, Email*, Telefoon, KvK</Text>
                                </Box>
                                <Box sx={{ mt: 1, ml: 1 }}>
                                    <Text size="fs12" bold>{t("sheet_name")}:&nbsp;</Text>
                                    <Text size="fs12" light>producten</Text>
                                </Box>
                                <Box sx={{ mt: 0.5, ml: 1 }}>
                                    <Text size="fs11" bold>{t("headers")}:&nbsp;</Text>
                                    <Text size="fs11" light>SKU*, Klant*, Type (virtueel/normaal/leeg), Omschrijving*, Barcode, Gewicht, Lengte, Breedte, Hoogte, HS code, Land van oorsprong (2-letterige landcode), Kostprijs, Standaard drager, Veiligheidsvoorraad, Kritische voorraad, Inactief (ja/j)</Text>
                                </Box>
                                <Box sx={{ mt: 1, ml: 1 }}>
                                    <Text size="fs12" bold>{t("sheet_name")}:&nbsp;</Text>
                                    <Text size="fs12" light>locaties</Text>
                                </Box>
                                <Box sx={{ mt: 0.5, ml: 1 }}>
                                    <Text size="fs11" bold>{t("headers")}:&nbsp;</Text>
                                    <Text size="fs11" light>Magazijn, Magazijnlocatie*, Zone*, Locatiesoort*</Text>
                                </Box>
                                <Box sx={{ mt: 1, ml: 1 }}>
                                    <Text size="fs12" bold>{t("sheet_name")}:&nbsp;</Text>
                                    <Text size="fs12" light>voorraad</Text>
                                </Box>
                                <Box sx={{ mt: 0.5, ml: 1 }}>
                                    <Text size="fs11" bold>{t("headers")}:&nbsp;</Text>
                                    <Text size="fs11" light>Magazijn, Magazijnlocatie*, Klant*, SKU*, Voorraad*, THT-datum, Batchnummer, Partijnummer</Text>
                                </Box>
                                <Box sx={{ mt: 1, ml: 1 }}>
                                    <Text size="fs12" bold>{t("sheet_name")}:&nbsp;</Text>
                                    <Text size="fs12" light>inkoop</Text>
                                </Box>
                                <Box sx={{ mt: 0.5, ml: 1 }}>
                                    <Text size="fs11" bold>{t("headers")}:&nbsp;</Text>
                                    <Text size="fs11" light>Magazijn, Magazijnlocatie*, Klant*, Referentie*, SKU*, Verwacht aantal*, Ontvangen</Text>
                                </Box>
                                <Box sx={{ mt: 1, ml: 1 }}>
                                    <Text size="fs12" bold>{t("sheet_name")}:&nbsp;</Text>
                                    <Text size="fs12" light>bestellingen</Text>
                                </Box>
                                <Box sx={{ mt: 0.5, ml: 1 }}>
                                    <Text size="fs11" bold>{t("headers")}:&nbsp;</Text>
                                    <Text size="fs11" light>Klant*, Webshopreferentie*, Geplande datum, Afhalen, B2B, Voornaam*, Achternaam*, Bedrijf, Telefoon, Email*, Adresregel*, Tweede adresregel, Postcode*, Stad*, Landcode*, SKU*, Aantal*</Text>
                                </Box>
                            </Box>
                        </Box>
                    }
                >
                    {({ onMouseEnter, onMouseLeave, onTouchStart }) => (
                        <Box
                            sx={{
                                display:"flex",
                                flexDirection:"column",
                                overflow:"hidden",
                                cursor:"pointer"
                            }}
                            onMouseEnter={onMouseEnter}
                            onMouseLeave={onMouseLeave}
                            onTouchStart={onTouchStart}
                        >
                            <Text
                                sx={props.explainer ? {display: "flex", alignItems: "center"} : {}}
                                noBreak
                                size={"fs13"} >
                                {selectedFile ? `Stockhub import: ${selectedFile.name}` : "Stockhub import"}
                            </Text>
                            <Text
                                light
                                sx={{
                                pt:1,
                                }}
                            >
                               {t("upload_stockhub_file")}
                            </Text>
                        </Box>
                    )}
                </SHQuickAction>
                <Shbutton className={"always-visible"} onClick={() => handleClick("stockhub")} color="primary" variant="text" startIcon={<FileUploadIcon/>} disabled={isUploading}>
                    {selectedFile ? t("uploaded") : t("upload")}
                </Shbutton>
            </Box>
            </Box>

            <Box
            sx={{
                width: 1,
            }}
            >
            <Box
                sx={{
                display:"none",
                "@media screen and (max-width: 47.9375em)": {
                    display:"block"
                },
                }}
            >
                <Box
                sx={{
                    ml:"-12px",
                    mr:"-12px",
                    pl:2,
                    pr:2,
                    display:"flex",
                    borderRadius:7,
                    gap:2,
                    alignItems:"center",
                    justifyContent:"space-between",
                    background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                    border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                    boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                }}
                >
                <Text
                    sx={props.explainer ? {display: "flex", alignItems: "center"} : {}} 
                    noBreak 
                    size={"fs13"} >
                    {selectedFile ? `Lyra import: ${selectedFile.name}` : "Lyra import"}
                </Text>
                </Box>
                <Text 
                    light
                    sx={{
                    pt:1,
                    }}
                >
                    {t("upload_lyra_file")}
                </Text>
                <Shbutton onClick={() => handleClick("lyra")} color="primary" variant="text" startIcon={<FileUploadIcon/>} disabled={isUploading}>
                    {selectedFile ? t("uploaded") : t("upload")}
                </Shbutton>
            </Box>
            <Box
                sx={{
                width: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 4,
                cursor: 'pointer',
                "@media screen and (max-width: 47.9375em)": {
                    display:"none"
                },
                }}
            >
                <Box
                sx={{
                    display:"flex",
                    flexDirection:"column",
                    overflow:"hidden",
                    cursor:"pointer"
                }}
                >
                <Text
                    sx={props.explainer ? {display: "flex", alignItems: "center"} : {}} 
                    noBreak 
                    size={"fs13"} >
                    {selectedFile ? `Lyra import: ${selectedFile.name}` : "Lyra import"}
                </Text>
                <Text 
                    light
                    sx={{
                    pt:1,
                    }}
                >
                    {t("upload_lyra_file")}
                </Text>
                </Box>
                <Shbutton className={"always-visible"} onClick={() => handleClick("lyra")} color="primary" variant="text" startIcon={<FileUploadIcon/>} disabled={isUploading}>
                    {selectedFile ? t("uploaded") : t("upload")}
                </Shbutton>
            </Box>
            </Box>

            <Box
            sx={{
                width: 1,
            }}
            >
            <Box
                sx={{
                display:"none",
                "@media screen and (max-width: 47.9375em)": {
                    display:"block"
                },
                }}
            >
                <Box
                sx={{
                    ml:"-12px",
                    mr:"-12px",
                    pl:2,
                    pr:2,
                    display:"flex",
                    borderRadius:7,
                    gap:2,
                    alignItems:"center",
                    justifyContent:"space-between",
                    background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                    border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                    boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                }}
                >
                <Text
                    sx={props.explainer ? {display: "flex", alignItems: "center"} : {}} 
                    noBreak 
                    size={"fs13"} >
                    {selectedFile ? `Financieel import: ${selectedFile.name}` : "Financieel import"}
                </Text>
                </Box>
                <Text 
                    light
                    sx={{
                    pt:1,
                    }}
                >
                    {t("upload_financial_file")}
                </Text>
                <Shbutton onClick={() => handleClick("invoicing")} color="primary" variant="text" startIcon={<FileUploadIcon/>} disabled={isUploading}>
                    {selectedFile ? t("uploaded") : t("upload")}
                </Shbutton>
            </Box>
            <Box
                sx={{
                width: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 4,
                cursor: 'pointer',
                "@media screen and (max-width: 47.9375em)": {
                    display:"none"
                },
                }}
            >
                <Box
                sx={{
                    display:"flex",
                    flexDirection:"column",
                    overflow:"hidden",
                    cursor:"pointer"
                }}
                >
                <Text
                    sx={props.explainer ? {display: "flex", alignItems: "center"} : {}} 
                    noBreak 
                    size={"fs13"} >
                    {selectedFile ? `Financieel import: ${selectedFile.name}` : "Financieel import"}
                </Text>
                <Text 
                    light
                    sx={{
                    pt:1,
                    }}
                >
                    {t("upload_financial_file")}
                </Text>
                </Box>
                <Shbutton className={"always-visible"} onClick={() => handleClick("invoicing")} color="primary" variant="text" startIcon={<FileUploadIcon/>} disabled={isUploading}>
                    {selectedFile ? t("uploaded") : t("upload")}
                </Shbutton>
            </Box>
            </Box>

            <Box
                sx={{
                width: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 4,
                marginTop: 1,
                marginBottom: 1,
                cursor: 'pointer',
                "@media screen and (max-width: 47.9375em)": {
                    display:"none"
                },
                }}
            >
                <Text
                    sx={props.explainer ? {display: "flex", alignItems: "center"} : {}} 
                    noBreak 
                    fontSize={16} bold >
                    {t("individual_imports")}
                </Text>
            </Box>
            {/* Single Import: Klanten */}
            <Box sx={{
                width: 1,
                p: 2,
                border: `1px solid ${colors.grey[200]}`,
                borderRadius: 2
            }}>
            <Box
                sx={{
                display:"none",
                "@media screen and (max-width: 47.9375em)": {
                    display:"block"
                },
                }}
            >
                <Box
                sx={{
                    ml:"-12px",
                    mr:"-12px",
                    pl:2,
                    pr:2,
                    display:"flex",
                    borderRadius:7,
                    gap:2,
                    alignItems:"center",
                    justifyContent:"space-between",
                    background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                    border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                    boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                }}
                >
                <Text
                    sx={props.explainer ? {display: "flex", alignItems: "center"} : {}}
                    noBreak
                    size={"fs13"} >
                    {t("import_klanten")}
                </Text>
                </Box>
                <Text
                    light
                    sx={{
                    pt:1,
                    }}
                >
                    {t("upload_customers_file")}
                </Text>
                <Shbutton onClick={() => handleSingleClick("klanten")} color="primary" variant="text" startIcon={<FileUploadIcon/>} disabled={isUploading}>
                    {t("upload")}
                </Shbutton>
            </Box>
            <Box
                sx={{
                width: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 4,
                cursor: 'pointer',
                "@media screen and (max-width: 47.9375em)": {
                    display:"none"
                },
                }}
            >
                <SHQuickAction
                    component={
                        <Box
                            sx={{
                                px:1.5,
                                display:"flex",
                                flexDirection:"column"
                            }}
                        >
                            <Text bold>{t("requirements_import_file")}</Text>
                            <Box sx={{ mt: 1 }}>
                                <Text size="fs12" bold>{t("sheet_name")}:&nbsp;</Text>
                                <Text size="fs12" light>klanten</Text>
                            </Box>
                            <Box sx={{ mt: 0.5, ml: 1 }}>
                                <Text size="fs11" bold>{t("headers")}:&nbsp;</Text>
                                <Text size="fs11" light>Klant*, Naam contactpersoon*, Straat, Huisnummer, Toevoeging, Postcode, Plaats, Email*, Telefoon, KvK</Text>
                            </Box>
                        </Box>
                    }
                >
                    {({ onMouseEnter, onMouseLeave, onTouchStart }) => (
                        <Box
                            sx={{
                                display:"flex",
                                flexDirection:"column",
                                overflow:"hidden",
                                cursor:"pointer"
                            }}
                            onMouseEnter={onMouseEnter}
                            onMouseLeave={onMouseLeave}
                            onTouchStart={onTouchStart}
                        >
                            <Text
                                sx={props.explainer ? {display: "flex", alignItems: "center"} : {}}
                                noBreak
                                size={"fs13"} >
                                {t("import_klanten")}
                            </Text>
                            <Text
                                light
                                sx={{
                                pt:1,
                                }}
                            >
                                {t("upload_customers_file")}
                            </Text>
                        </Box>
                    )}
                </SHQuickAction>
                <Shbutton className={"always-visible"} onClick={() => handleSingleClick("klanten")} color="primary" variant="text" startIcon={<FileUploadIcon/>} disabled={isUploading}>
                    {t("upload")}
                </Shbutton>
            </Box>
            </Box>

            {/* Single Import: Producten */}
            <Box sx={{
                width: 1,
                p: 2,
                border: `1px solid ${colors.grey[200]}`,
                borderRadius: 2
            }}>
            <Box
                sx={{
                display:"none",
                "@media screen and (max-width: 47.9375em)": {
                    display:"block"
                },
                }}
            >
                <Box
                sx={{
                    ml:"-12px",
                    mr:"-12px",
                    pl:2,
                    pr:2,
                    display:"flex",
                    borderRadius:7,
                    gap:2,
                    alignItems:"center",
                    justifyContent:"space-between",
                    background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                    border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                    boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                }}
                >
                <Text
                    sx={props.explainer ? {display: "flex", alignItems: "center"} : {}}
                    noBreak
                    size={"fs13"} >
                    {t("import_producten")}
                </Text>
                </Box>
                <Text
                    light
                    sx={{
                    pt:1,
                    }}
                >
                    {t("upload_products_file")}
                </Text>
                <Shbutton onClick={() => handleSingleClick("producten")} color="primary" variant="text" startIcon={<FileUploadIcon/>} disabled={isUploading}>
                    {t("upload")}
                </Shbutton>
            </Box>
            <Box
                sx={{
                width: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 4,
                cursor: 'pointer',
                "@media screen and (max-width: 47.9375em)": {
                    display:"none"
                },
                }}
            >
                <SHQuickAction
                    component={
                        <Box
                            sx={{
                                px:1.5,
                                display:"flex",
                                flexDirection:"column"
                            }}
                        >
                            <Text bold>{t("requirements_import_file")}</Text>
                            <Box sx={{ mt: 1 }}>
                                <Text size="fs12" bold>{t("sheet_name")}:&nbsp;</Text>
                                <Text size="fs12" light>producten</Text>
                            </Box>
                            <Box sx={{ mt: 0.5, ml: 1 }}>
                                <Text size="fs11" bold>{t("headers")}:&nbsp;</Text>
                                <Text size="fs11" light>SKU*, Klant*, Type (virtueel/normaal/leeg), Omschrijving*, Barcode, Gewicht, Lengte, Breedte, Hoogte, HS code, Land van oorsprong (2-letterige landcode), Kostprijs, Standaard drager, Veiligheidsvoorraad, Kritische voorraad, Inactief (ja/j)</Text>
                            </Box>
                        </Box>
                    }
                >
                    {({ onMouseEnter, onMouseLeave, onTouchStart }) => (
                        <Box
                            sx={{
                                display:"flex",
                                flexDirection:"column",
                                overflow:"hidden",
                                cursor:"pointer"
                            }}
                            onMouseEnter={onMouseEnter}
                            onMouseLeave={onMouseLeave}
                            onTouchStart={onTouchStart}
                        >
                            <Text
                                sx={props.explainer ? {display: "flex", alignItems: "center"} : {}}
                                noBreak
                                size={"fs13"} >
                                {t("import_producten")}
                            </Text>
                            <Text
                                light
                                sx={{
                                pt:1,
                                }}
                            >
                                {t("upload_products_file")}
                            </Text>
                        </Box>
                    )}
                </SHQuickAction>
                <Shbutton className={"always-visible"} onClick={() => handleSingleClick("producten")} color="primary" variant="text" startIcon={<FileUploadIcon/>} disabled={isUploading}>
                    {t("upload")}
                </Shbutton>
            </Box>
            </Box>

            {/* Single Import: Samenstellingen */}
            <Box sx={{
                width: 1,
                p: 2,
                border: `1px solid ${colors.grey[200]}`,
                borderRadius: 2
            }}>
            <Box
                sx={{
                display:"none",
                "@media screen and (max-width: 47.9375em)": {
                    display:"block"
                },
                }}
            >
                <Box
                sx={{
                    ml:"-12px",
                    mr:"-12px",
                    pl:2,
                    pr:2,
                    display:"flex",
                    borderRadius:7,
                    gap:2,
                    alignItems:"center",
                    justifyContent:"space-between",
                    background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                    border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                    boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                }}
                >
                <Text
                    sx={props.explainer ? {display: "flex", alignItems: "center"} : {}}
                    noBreak
                    size={"fs13"} >
                    {t("import_samenstellingen")}
                </Text>
                </Box>
                <Text
                    light
                    sx={{
                    pt:1,
                    }}
                >
                    {t("upload_virtual_products_file")}
                </Text>
                <Shbutton onClick={() => handleSingleClick("samenstellingen")} color="primary" variant="text" startIcon={<FileUploadIcon/>} disabled={isUploading}>
                    {t("upload")}
                </Shbutton>
            </Box>
            <Box
                sx={{
                width: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 4,
                cursor: 'pointer',
                "@media screen and (max-width: 47.9375em)": {
                    display:"none"
                },
                }}
            >
                <SHQuickAction
                    component={
                        <Box
                            sx={{
                                px:1.5,
                                display:"flex",
                                flexDirection:"column"
                            }}
                        >
                            <Text bold>{t("requirements_import_file")}</Text>
                            <Box sx={{ mt: 1 }}>
                                <Text size="fs12" bold>{t("sheet_name")}:&nbsp;</Text>
                                <Text size="fs12" light>samenstellingen</Text>
                            </Box>
                            <Box sx={{ mt: 0.5, ml: 1 }}>
                                <Text size="fs11" bold>{t("headers")}:&nbsp;</Text>
                                <Text size="fs11" light>Klant*, Product SKU*, Onderdeel SKU*, Hoeveelheid*</Text>
                            </Box>
                        </Box>
                    }
                >
                    {({ onMouseEnter, onMouseLeave, onTouchStart }) => (
                        <Box
                            sx={{
                                display:"flex",
                                flexDirection:"column",
                                overflow:"hidden",
                                cursor:"pointer"
                            }}
                            onMouseEnter={onMouseEnter}
                            onMouseLeave={onMouseLeave}
                            onTouchStart={onTouchStart}
                        >
                            <Text
                                sx={props.explainer ? {display: "flex", alignItems: "center"} : {}}
                                noBreak
                                size={"fs13"} >
                                {t("import_samenstellingen")}
                            </Text>
                            <Text
                                light
                                sx={{
                                pt:1,
                                }}
                            >
                                {t("upload_virtual_products_file")}
                            </Text>
                        </Box>
                    )}
                </SHQuickAction>
                <Shbutton className={"always-visible"} onClick={() => handleSingleClick("samenstellingen")} color="primary" variant="text" startIcon={<FileUploadIcon/>} disabled={isUploading}>
                    {t("upload")}
                </Shbutton>
            </Box>
            </Box>

            {/* Single Import: Locaties */}
            <Box sx={{
                width: 1,
                p: 2,
                border: `1px solid ${colors.grey[200]}`,
                borderRadius: 2
            }}>
            <Box
                sx={{
                display:"none",
                "@media screen and (max-width: 47.9375em)": {
                    display:"block"
                },
                }}
            >
                <Box
                sx={{
                    ml:"-12px",
                    mr:"-12px",
                    pl:2,
                    pr:2,
                    display:"flex",
                    borderRadius:7,
                    gap:2,
                    alignItems:"center",
                    justifyContent:"space-between",
                    background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                    border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                    boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                }}
                >
                <Text
                    sx={props.explainer ? {display: "flex", alignItems: "center"} : {}}
                    noBreak
                    size={"fs13"} >
                    {t("import_locaties")}
                </Text>
                </Box>
                <Text
                    light
                    sx={{
                    pt:1,
                    }}
                >
                    {t("upload_locations_file")}
                </Text>
                <Shbutton onClick={() => handleSingleClick("locaties")} color="primary" variant="text" startIcon={<FileUploadIcon/>} disabled={isUploading}>
                    {t("upload")}
                </Shbutton>
            </Box>
            <Box
                sx={{
                width: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 4,
                cursor: 'pointer',
                "@media screen and (max-width: 47.9375em)": {
                    display:"none"
                },
                }}
            >
                <SHQuickAction
                    component={
                        <Box
                            sx={{
                                px:1.5,
                                display:"flex",
                                flexDirection:"column"
                            }}
                        >
                            <Text bold>{t("requirements_import_file")}</Text>
                            <Box sx={{ mt: 1 }}>
                                <Text size="fs12" bold>{t("sheet_name")}:&nbsp;</Text>
                                <Text size="fs12" light>locaties</Text>
                            </Box>
                            <Box sx={{ mt: 0.5, ml: 1 }}>
                                <Text size="fs11" bold>{t("headers")}:&nbsp;</Text>
                                <Text size="fs11" light>Magazijn, Magazijnlocatie*, Zone*, Locatiesoort*</Text>
                            </Box>
                        </Box>
                    }
                >
                    {({ onMouseEnter, onMouseLeave, onTouchStart }) => (
                        <Box
                            sx={{
                                display:"flex",
                                flexDirection:"column",
                                overflow:"hidden",
                                cursor:"pointer"
                            }}
                            onMouseEnter={onMouseEnter}
                            onMouseLeave={onMouseLeave}
                            onTouchStart={onTouchStart}
                        >
                            <Text
                                sx={props.explainer ? {display: "flex", alignItems: "center"} : {}}
                                noBreak
                                size={"fs13"} >
                                {t("import_locaties")}
                            </Text>
                            <Text
                                light
                                sx={{
                                pt:1,
                                }}
                            >
                                {t("upload_locations_file")}
                            </Text>
                        </Box>
                    )}
                </SHQuickAction>
                <Shbutton className={"always-visible"} onClick={() => handleSingleClick("locaties")} color="primary" variant="text" startIcon={<FileUploadIcon/>} disabled={isUploading}>
                    {t("upload")}
                </Shbutton>
            </Box>
            </Box>

            {/* Single Import: Voorraad */}
            <Box sx={{
                width: 1,
                p: 2,
                border: `1px solid ${colors.grey[200]}`,
                borderRadius: 2
            }}>
            <Box
                sx={{
                display:"none",
                "@media screen and (max-width: 47.9375em)": {
                    display:"block"
                },
                }}
            >
                <Box
                sx={{
                    ml:"-12px",
                    mr:"-12px",
                    pl:2,
                    pr:2,
                    display:"flex",
                    borderRadius:7,
                    gap:2,
                    alignItems:"center",
                    justifyContent:"space-between",
                    background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                    border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                    boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                }}
                >
                <Text
                    sx={props.explainer ? {display: "flex", alignItems: "center"} : {}}
                    noBreak
                    size={"fs13"} >
                    {t("import_voorraad")}
                </Text>
                </Box>
                <Text
                    light
                    sx={{
                    pt:1,
                    }}
                >
                    {t("upload_stock_file")}
                </Text>
                <Shbutton onClick={() => handleSingleClick("voorraad")} color="primary" variant="text" startIcon={<FileUploadIcon/>} disabled={isUploading}>
                    {t("upload")}
                </Shbutton>
            </Box>
            <Box
                sx={{
                width: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 4,
                cursor: 'pointer',
                "@media screen and (max-width: 47.9375em)": {
                    display:"none"
                },
                }}
            >
                <SHQuickAction
                    component={
                        <Box
                            sx={{
                                px:1.5,
                                display:"flex",
                                flexDirection:"column"
                            }}
                        >
                            <Text bold>{t("requirements_import_file")}</Text>
                            <Box sx={{ mt: 1 }}>
                                <Text size="fs12" bold>{t("sheet_name")}:&nbsp;</Text>
                                <Text size="fs12" light>voorraad</Text>
                            </Box>
                            <Box sx={{ mt: 0.5, ml: 1 }}>
                                <Text size="fs11" bold>{t("headers")}:&nbsp;</Text>
                                <Text size="fs11" light>Magazijn, Magazijnlocatie*, Klant*, SKU*, Voorraad*, THT-datum, Batchnummer, Partijnummer</Text>
                            </Box>
                        </Box>
                    }
                >
                    {({ onMouseEnter, onMouseLeave, onTouchStart }) => (
                        <Box
                            sx={{
                                display:"flex",
                                flexDirection:"column",
                                overflow:"hidden",
                                cursor:"pointer"
                            }}
                            onMouseEnter={onMouseEnter}
                            onMouseLeave={onMouseLeave}
                            onTouchStart={onTouchStart}
                        >
                            <Text
                                sx={props.explainer ? {display: "flex", alignItems: "center"} : {}}
                                noBreak
                                size={"fs13"} >
                                {t("import_voorraad")}
                            </Text>
                            <Text
                                light
                                sx={{
                                pt:1,
                                }}
                            >
                                {t("upload_stock_file")}
                            </Text>
                        </Box>
                    )}
                </SHQuickAction>
                <Shbutton className={"always-visible"} onClick={() => handleSingleClick("voorraad")} color="primary" variant="text" startIcon={<FileUploadIcon/>} disabled={isUploading}>
                    {t("upload")}
                </Shbutton>
            </Box>
            </Box>

            {/* Single Import: Voorraad verplaatsen */}
            <Box sx={{
                width: 1,
                p: 2,
                border: `1px solid ${colors.grey[200]}`,
                borderRadius: 2
            }}>
            <Box
                sx={{
                display:"none",
                "@media screen and (max-width: 47.9375em)": {
                    display:"block"
                },
                }}
            >
                <Box
                sx={{
                    ml:"-12px",
                    mr:"-12px",
                    pl:2,
                    pr:2,
                    display:"flex",
                    borderRadius:7,
                    gap:2,
                    alignItems:"center",
                    justifyContent:"space-between",
                    background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                    border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                    boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                }}
                >
                <Text
                    sx={props.explainer ? {display: "flex", alignItems: "center"} : {}}
                    noBreak
                    size={"fs13"} >
                    {t("import_voorraad_verplaatsen")}
                </Text>
                </Box>
                <Text
                    light
                    sx={{
                    pt:1,
                    }}
                >
                    {t("upload_stock_move_file")}
                </Text>
                <Shbutton onClick={() => handleSingleClick("voorraadverplaatsen")} color="primary" variant="text" startIcon={<FileUploadIcon/>} disabled={isUploading}>
                    {t("upload")}
                </Shbutton>
            </Box>
            <Box
                sx={{
                width: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 4,
                cursor: 'pointer',
                "@media screen and (max-width: 47.9375em)": {
                    display:"none"
                },
                }}
            >
                <SHQuickAction
                    component={
                        <Box
                            sx={{
                                px:1.5,
                                display:"flex",
                                flexDirection:"column"
                            }}
                        >
                            <Text bold>{t("requirements_import_file")}</Text>
                            <Box sx={{ mt: 1 }}>
                                <Text size="fs12" bold>{t("sheet_name")}:&nbsp;</Text>
                                <Text size="fs12" light>voorraad</Text>
                            </Box>
                            <Box sx={{ mt: 0.5, ml: 1 }}>
                                <Text size="fs11" bold>{t("headers")}:&nbsp;</Text>
                                <Text size="fs11" light>Magazijn oud*, Magazijn nieuw*, Magazijnlocatie oud*, Magazijnlocatie nieuw*, SKU*</Text>
                            </Box>
                        </Box>
                    }
                >
                    {({ onMouseEnter, onMouseLeave, onTouchStart }) => (
                        <Box
                            sx={{
                                display:"flex",
                                flexDirection:"column",
                                overflow:"hidden",
                                cursor:"pointer"
                            }}
                            onMouseEnter={onMouseEnter}
                            onMouseLeave={onMouseLeave}
                            onTouchStart={onTouchStart}
                        >
                            <Text
                                sx={props.explainer ? {display: "flex", alignItems: "center"} : {}}
                                noBreak
                                size={"fs13"} >
                                {t("import_voorraad_verplaatsen")}
                            </Text>
                            <Text
                                light
                                sx={{
                                pt:1,
                                }}
                            >
                                {t("upload_stock_move_file")}
                            </Text>
                        </Box>
                    )}
                </SHQuickAction>
                <Shbutton className={"always-visible"} onClick={() => handleSingleClick("voorraadverplaatsen")} color="primary" variant="text" startIcon={<FileUploadIcon/>} disabled={isUploading}>
                    {t("upload")}
                </Shbutton>
            </Box>
            </Box>

            {/* Single Import: Inkoop */}
            <Box sx={{
                width: 1,
                p: 2,
                border: `1px solid ${colors.grey[200]}`,
                borderRadius: 2
            }}>
            <Box
                sx={{
                display:"none",
                "@media screen and (max-width: 47.9375em)": {
                    display:"block"
                },
                }}
            >
                <Box
                sx={{
                    ml:"-12px",
                    mr:"-12px",
                    pl:2,
                    pr:2,
                    display:"flex",
                    borderRadius:7,
                    gap:2,
                    alignItems:"center",
                    justifyContent:"space-between",
                    background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                    border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                    boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                }}
                >
                <Text
                    sx={props.explainer ? {display: "flex", alignItems: "center"} : {}}
                    noBreak
                    size={"fs13"} >
                    {t("import_inkoop")}
                </Text>
                </Box>
                <Text
                    light
                    sx={{
                    pt:1,
                    }}
                >
                    {t("upload_inbound_file")}
                </Text>
                <Shbutton onClick={() => handleSingleClick("inkoop")} color="primary" variant="text" startIcon={<FileUploadIcon/>} disabled={isUploading}>
                    {t("upload")}
                </Shbutton>
            </Box>
            <Box
                sx={{
                width: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 4,
                cursor: 'pointer',
                "@media screen and (max-width: 47.9375em)": {
                    display:"none"
                },
                }}
            >
                <SHQuickAction
                    component={
                        <Box
                            sx={{
                                px:1.5,
                                display:"flex",
                                flexDirection:"column"
                            }}
                        >
                            <Text bold>{t("requirements_import_file")}</Text>
                            <Box sx={{ mt: 1 }}>
                                <Text size="fs12" bold>{t("sheet_name")}:&nbsp;</Text>
                                <Text size="fs12" light>inkoop</Text>
                            </Box>
                            <Box sx={{ mt: 0.5, ml: 1 }}>
                                <Text size="fs11" bold>{t("headers")}:&nbsp;</Text>
                                <Text size="fs11" light>Magazijn, Magazijnlocatie*, Klant*, Referentie*, SKU*, Verwacht aantal*, Ontvangen</Text>
                            </Box>
                        </Box>
                    }
                >
                    {({ onMouseEnter, onMouseLeave, onTouchStart }) => (
                        <Box
                            sx={{
                                display:"flex",
                                flexDirection:"column",
                                overflow:"hidden",
                                cursor:"pointer"
                            }}
                            onMouseEnter={onMouseEnter}
                            onMouseLeave={onMouseLeave}
                            onTouchStart={onTouchStart}
                        >
                            <Text
                                sx={props.explainer ? {display: "flex", alignItems: "center"} : {}}
                                noBreak
                                size={"fs13"} >
                                {t("import_inkoop")}
                            </Text>
                            <Text
                                light
                                sx={{
                                pt:1,
                                }}
                            >
                                {t("upload_inbound_file")}
                            </Text>
                        </Box>
                    )}
                </SHQuickAction>
                <Shbutton className={"always-visible"} onClick={() => handleSingleClick("inkoop")} color="primary" variant="text" startIcon={<FileUploadIcon/>} disabled={isUploading}>
                    {t("upload")}
                </Shbutton>
            </Box>
            </Box>

            {/* Single Import: Bestellingen */}
            <Box sx={{
                width: 1,
                p: 2,
                border: `1px solid ${colors.grey[200]}`,
                borderRadius: 2
            }}>
            <Box
                sx={{
                display:"none",
                "@media screen and (max-width: 47.9375em)": {
                    display:"block"
                },
                }}
            >
                <Box
                sx={{
                    ml:"-12px",
                    mr:"-12px",
                    pl:2,
                    pr:2,
                    display:"flex",
                    borderRadius:7,
                    gap:2,
                    alignItems:"center",
                    justifyContent:"space-between",
                    background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                    border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                    boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                }}
                >
                <Text
                    sx={props.explainer ? {display: "flex", alignItems: "center"} : {}}
                    noBreak
                    size={"fs13"} >
                    {t("import_bestellingen")}
                </Text>
                </Box>
                <Text
                    light
                    sx={{
                    pt:1,
                    }}
                >
                    {t("upload_order_file")}
                </Text>
                <Shbutton onClick={() => handleSingleClick("bestellingen")} color="primary" variant="text" startIcon={<FileUploadIcon/>} disabled={isUploading}>
                    {t("upload")}
                </Shbutton>
            </Box>
            <Box
                sx={{
                width: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 4,
                cursor: 'pointer',
                "@media screen and (max-width: 47.9375em)": {
                    display:"none"
                },
                }}
            >
                <SHQuickAction
                    component={
                        <Box
                            sx={{
                                px:1.5,
                                display:"flex",
                                flexDirection:"column"
                            }}
                        >
                            <Text bold>{t("requirements_import_file")}</Text>
                            <Box sx={{ mt: 1 }}>
                                <Text size="fs12" bold>{t("sheet_name")}:&nbsp;</Text>
                                <Text size="fs12" light>bestellingen</Text>
                            </Box>
                            <Box sx={{ mt: 0.5, ml: 1 }}>
                                <Text size="fs11" bold>{t("headers")}:&nbsp;</Text>
                                <Text size="fs11" light>Klant*, Webshopreferentie*, Geplande datum, Afhalen, B2B, Voornaam*, Achternaam*, Bedrijf, Telefoon, Email*, Adresregel*, Tweede adresregel, Postcode*, Stad*, Landcode*, SKU*, Aantal*</Text>
                            </Box>
                        </Box>
                    }
                >
                    {({ onMouseEnter, onMouseLeave, onTouchStart }) => (
                        <Box
                            sx={{
                                display:"flex",
                                flexDirection:"column",
                                overflow:"hidden",
                                cursor:"pointer"
                            }}
                            onMouseEnter={onMouseEnter}
                            onMouseLeave={onMouseLeave}
                            onTouchStart={onTouchStart}
                        >
                            <Text
                                sx={props.explainer ? {display: "flex", alignItems: "center"} : {}}
                                noBreak
                                size={"fs13"} >
                                {t("import_bestellingen")}
                            </Text>
                            <Text
                                light
                                sx={{
                                pt:1,
                                }}
                            >
                                {t("upload_inbound_file")}
                            </Text>
                        </Box>
                    )}
                </SHQuickAction>
                <Shbutton className={"always-visible"} onClick={() => handleSingleClick("bestellingen")} color="primary" variant="text" startIcon={<FileUploadIcon/>} disabled={isUploading}>
                    {t("upload")}
                </Shbutton>
            </Box>
            </Box>

            {/* Single Import: Naheffingen */}
            <Box sx={{
                width: 1,
                p: 2,
                border: `1px solid ${colors.grey[200]}`,
                borderRadius: 2
            }}>
            <Box
                sx={{
                display:"none",
                "@media screen and (max-width: 47.9375em)": {
                    display:"block"
                },
                }}
            >
                <Box
                sx={{
                    ml:"-12px",
                    mr:"-12px",
                    pl:2,
                    pr:2,
                    display:"flex",
                    borderRadius:7,
                    gap:2,
                    alignItems:"center",
                    justifyContent:"space-between",
                    background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                    border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                    boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                }}
                >
                <Text
                    sx={props.explainer ? {display: "flex", alignItems: "center"} : {}}
                    noBreak
                    size={"fs13"} >
                    {t("import_naheffing")}
                </Text>
                </Box>
                <Text
                    light
                    sx={{
                    pt:1,
                    }}
                >
                    {t("upload_adjustmentcharges_file")}
                </Text>
                <Shbutton onClick={() => handleSingleClick("naheffingen")} color="primary" variant="text" startIcon={<FileUploadIcon/>} disabled={isUploading}>
                    {t("upload")}
                </Shbutton>
            </Box>
            <Box
                sx={{
                width: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 4,
                cursor: 'pointer',
                "@media screen and (max-width: 47.9375em)": {
                    display:"none"
                },
                }}
            >
                <SHQuickAction
                    component={
                        <Box
                            sx={{
                                px:1.5,
                                display:"flex",
                                flexDirection:"column"
                            }}
                        >
                            <Text bold>{t("requirements_import_file")}</Text>
                            <Box sx={{ mt: 1 }}>
                                <Text size="fs12" light>{t("adjustment_charges_import_explainer")}</Text>
                            </Box>
                        </Box>
                    }
                >
                    {({ onMouseEnter, onMouseLeave, onTouchStart }) => (
                        <Box
                            sx={{
                                display:"flex",
                                flexDirection:"column",
                                overflow:"hidden",
                                cursor:"pointer"
                            }}
                            onMouseEnter={onMouseEnter}
                            onMouseLeave={onMouseLeave}
                            onTouchStart={onTouchStart}
                        >
                            <Text
                                sx={props.explainer ? {display: "flex", alignItems: "center"} : {}}
                                noBreak
                                size={"fs13"} >
                                {t("import_naheffing")}
                            </Text>
                            <Text
                                light
                                sx={{
                                pt:1,
                                }}
                            >
                                {t("upload_adjustmentcharges_file")}
                            </Text>
                        </Box>
                    )}
                </SHQuickAction>
                <Shbutton className={"always-visible"} onClick={() => handleSingleClick("naheffingen")} color="primary" variant="text" startIcon={<FileUploadIcon/>} disabled={isUploading}>
                    {t("upload")}
                </Shbutton>
            </Box>
            </Box>

            {/* Single Import: Picqer backorders */}
            <Box sx={{
                width: 1,
                p: 2,
                border: `1px solid ${colors.grey[200]}`,
                borderRadius: 2
            }}>
            <Box
                sx={{
                display:"none",
                "@media screen and (max-width: 47.9375em)": {
                    display:"block"
                },
                }}
            >
                <Box
                sx={{
                    ml:"-12px",
                    mr:"-12px",
                    pl:2,
                    pr:2,
                    display:"flex",
                    borderRadius:7,
                    gap:2,
                    alignItems:"center",
                    justifyContent:"space-between",
                    background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                    border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                    boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                }}
                >
                <Text
                    sx={props.explainer ? {display: "flex", alignItems: "center"} : {}}
                    noBreak
                    size={"fs13"} >
                    {t("import_picqer_backorders")}
                </Text>
                </Box>
                <Text
                    light
                    sx={{
                    pt:1,
                    }}
                >
                    {t("upload_picqerbackorder_file")}
                </Text>
                <Shbutton onClick={() => handleSingleClick("picqerbackorder")} color="primary" variant="text" startIcon={<FileUploadIcon/>} disabled={isUploading}>
                    {t("upload")}
                </Shbutton>
            </Box>
            <Box
                sx={{
                width: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 4,
                cursor: 'pointer',
                "@media screen and (max-width: 47.9375em)": {
                    display:"none"
                },
                }}
            >
                <SHQuickAction
                    component={
                        <Box
                            sx={{
                                px:1.5,
                                display:"flex",
                                flexDirection:"column"
                            }}
                        >
                            <Text bold>{t("requirements_import_file")}</Text>
                            <Box sx={{ mt: 1 }}>
                                <Text size="fs12" bold>{t("sheet_name")}:&nbsp;</Text>
                                <Text size="fs12" light>naheffingen</Text>
                            </Box>
                            <Box sx={{ mt: 0.5, ml: 1 }}>
                                <Text size="fs11" bold>{t("headers")}:&nbsp;</Text>
                                <Text size="fs11" light>Webshopreferentie*,Tracking*,Naheffing*,Omschrijving*</Text>
                            </Box>
                        </Box>
                    }
                >
                    {({ onMouseEnter, onMouseLeave, onTouchStart }) => (
                        <Box
                            sx={{
                                display:"flex",
                                flexDirection:"column",
                                overflow:"hidden",
                                cursor:"pointer"
                            }}
                            onMouseEnter={onMouseEnter}
                            onMouseLeave={onMouseLeave}
                            onTouchStart={onTouchStart}
                        >
                            <Text
                                sx={props.explainer ? {display: "flex", alignItems: "center"} : {}}
                                noBreak
                                size={"fs13"} >
                                {t("import_picqer_backorders")}
                            </Text>
                            <Text
                                light
                                sx={{
                                pt:1,
                                }}
                            >
                                {t("upload_picqerbackorder_file")}
                            </Text>
                        </Box>
                    )}
                </SHQuickAction>
                <Shbutton className={"always-visible"} onClick={() => handleSingleClick("picqerbackorder")} color="primary" variant="text" startIcon={<FileUploadIcon/>} disabled={isUploading}>
                    {t("upload")}
                </Shbutton>
            </Box>
            </Box>
            {/* Single Import: Picqer backorders */}
            <Box sx={{
                width: 1,
                p: 2,
                border: `1px solid ${colors.grey[200]}`,
                borderRadius: 2
            }}>
            <Box
                sx={{
                display:"none",
                "@media screen and (max-width: 47.9375em)": {
                    display:"block"
                },
                }}
            >
                <Box
                sx={{
                    ml:"-12px",
                    mr:"-12px",
                    pl:2,
                    pr:2,
                    display:"flex",
                    borderRadius:7,
                    gap:2,
                    alignItems:"center",
                    justifyContent:"space-between",
                    background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                    border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                    boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                }}
                >
                <Text
                    sx={props.explainer ? {display: "flex", alignItems: "center"} : {}}
                    noBreak
                    size={"fs13"} >
                    {t("import_picqerinbounds")}
                </Text>
                </Box>
                <Text
                    light
                    sx={{
                    pt:1,
                    }}
                >
                    {t("upload_picqerinbounds_file")}
                </Text>
                <Shbutton onClick={() => handleSingleClick("picqerinslag")} color="primary" variant="text" startIcon={<FileUploadIcon/>} disabled={isUploading}>
                    {t("upload")}
                </Shbutton>
            </Box>
            <Box
                sx={{
                width: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 4,
                cursor: 'pointer',
                "@media screen and (max-width: 47.9375em)": {
                    display:"none"
                },
                }}
            >
                <SHQuickAction
                    component={
                        <Box
                            sx={{
                                px:1.5,
                                display:"flex",
                                flexDirection:"column"
                            }}
                        >
                            <Text bold>{t("requirements_import_file")}</Text>
                            <Box sx={{ mt: 1 }}>
                                <Text size="fs12" bold>{t("sheet_name")}:&nbsp;</Text>
                                <Text size="fs12" light>naheffingen</Text>
                            </Box>
                            <Box sx={{ mt: 0.5, ml: 1 }}>
                                <Text size="fs11" bold>{t("headers")}:&nbsp;</Text>
                                <Text size="fs11" light>Webshopreferentie*,Tracking*,Naheffing*,Omschrijving*</Text>
                            </Box>
                        </Box>
                    }
                >
                    {({ onMouseEnter, onMouseLeave, onTouchStart }) => (
                        <Box
                            sx={{
                                display:"flex",
                                flexDirection:"column",
                                overflow:"hidden",
                                cursor:"pointer"
                            }}
                            onMouseEnter={onMouseEnter}
                            onMouseLeave={onMouseLeave}
                            onTouchStart={onTouchStart}
                        >
                            <Text
                                sx={props.explainer ? {display: "flex", alignItems: "center"} : {}}
                                noBreak
                                size={"fs13"} >
                                {t("import_picqerinbounds")}
                            </Text>
                            <Text
                                light
                                sx={{
                                pt:1,
                                }}
                            >
                                {t("upload_picqerinbounds_file")}
                            </Text>
                        </Box>
                    )}
                </SHQuickAction>
                <Shbutton className={"always-visible"} onClick={() => handleSingleClick("picqerinslag")} color="primary" variant="text" startIcon={<FileUploadIcon/>} disabled={isUploading}>
                    {t("upload")}
                </Shbutton>
            </Box>
            </Box>

            {/* Single Import: Invoiceproducts */}
            <Box sx={{
                width: 1,
                p: 2,
                border: `1px solid ${colors.grey[200]}`,
                borderRadius: 2
            }}>
            <Box
                sx={{
                display:"none",
                "@media screen and (max-width: 47.9375em)": {
                    display:"block"
                },
                }}
            >
                <Box
                sx={{
                    ml:"-12px",
                    mr:"-12px",
                    pl:2,
                    pr:2,
                    display:"flex",
                    borderRadius:7,
                    gap:2,
                    alignItems:"center",
                    justifyContent:"space-between",
                    background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                    border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                    boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                }}
                >
                <Text
                    sx={props.explainer ? {display: "flex", alignItems: "center"} : {}}
                    noBreak
                    size={"fs13"} >
                    {t("import_invoice_products")}
                </Text>
                </Box>
                <Text
                    light
                    sx={{
                    pt:1,
                    }}
                >
                    {t("upload_invoice_products_file")}
                </Text>
                <Shbutton onClick={() => handleSingleClick("tarieven")} color="primary" variant="text" startIcon={<FileUploadIcon/>} disabled={isUploading}>
                    {t("upload")}
                </Shbutton>
            </Box>
            <Box
                sx={{
                width: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 4,
                cursor: 'pointer',
                "@media screen and (max-width: 47.9375em)": {
                    display:"none"
                },
                }}
            >
                <SHQuickAction
                    component={
                        <Box
                            sx={{
                                px:1.5,
                                display:"flex",
                                flexDirection:"column"
                            }}
                        >
                            <Text bold>{t("requirements_import_file")}</Text>
                            <Box sx={{ mt: 1 }}>
                                <Text size="fs12" bold>{t("sheet_name")}:&nbsp;</Text>
                                <Text size="fs12" light>naheffingen</Text>
                            </Box>
                            <Box sx={{ mt: 0.5, ml: 1 }}>
                                <Text size="fs11" bold>{t("headers")}:&nbsp;</Text>
                                <Text size="fs11" light>Naam*, Nieuwe naam, Omschrijving factuurregel, Type, Grootboek, Belastingtarief</Text>
                            </Box>
                        </Box>
                    }
                >
                    {({ onMouseEnter, onMouseLeave, onTouchStart }) => (
                        <Box
                            sx={{
                                display:"flex",
                                flexDirection:"column",
                                overflow:"hidden",
                                cursor:"pointer"
                            }}
                            onMouseEnter={onMouseEnter}
                            onMouseLeave={onMouseLeave}
                            onTouchStart={onTouchStart}
                        >
                            <Text
                                sx={props.explainer ? {display: "flex", alignItems: "center"} : {}}
                                noBreak
                                size={"fs13"} >
                                {t("import_invoice_products")}
                            </Text>
                            <Text
                                light
                                sx={{
                                pt:1,
                                }}
                            >
                                {t("upload_invoice_products_file")}
                            </Text>
                        </Box>
                    )}
                </SHQuickAction>
                <Shbutton className={"always-visible"} onClick={() => handleSingleClick("tarieven")} color="primary" variant="text" startIcon={<FileUploadIcon/>} disabled={isUploading}>
                    {t("upload")}
                </Shbutton>
            </Box>
            </Box>

            {/* Single Import: Verzendkosten */}
            <Box sx={{
                width: 1,
                p: 2,
                border: `1px solid ${colors.grey[200]}`,
                borderRadius: 2
            }}>
            <Box
                sx={{
                display:"none",
                "@media screen and (max-width: 47.9375em)": {
                    display:"block"
                },
                }}
            >
                <Box
                sx={{
                    ml:"-12px",
                    mr:"-12px",
                    pl:2,
                    pr:2,
                    display:"flex",
                    borderRadius:7,
                    gap:2,
                    alignItems:"center",
                    justifyContent:"space-between",
                    background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                    border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                    boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                }}
                >
                <Text
                    sx={props.explainer ? {display: "flex", alignItems: "center"} : {}}
                    noBreak
                    size={"fs13"} >
                    {t("import_shipping_costs")}
                </Text>
                </Box>
                <Text
                    light
                    sx={{
                    pt:1,
                    }}
                >
                    {t("upload_shipping_costs_file")}
                </Text>
                <Shbutton onClick={() => handleSingleClick("verzendkosten")} color="primary" variant="text" startIcon={<FileUploadIcon/>} disabled={isUploading}>
                    {t("upload")}
                </Shbutton>
            </Box>
            <Box
                sx={{
                width: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 4,
                cursor: 'pointer',
                "@media screen and (max-width: 47.9375em)": {
                    display:"none"
                },
                }}
            >
                <SHQuickAction
                    component={
                        <Box
                            sx={{
                                px:1.5,
                                display:"flex",
                                flexDirection:"column"
                            }}
                        >
                            <Text bold>{t("requirements_import_file")}</Text>
                            <Box sx={{ mt: 1 }}>
                                <Text size="fs12" light>{t("shipping_costs_import_explainer")}</Text>
                            </Box>
                        </Box>
                    }
                >
                    {({ onMouseEnter, onMouseLeave, onTouchStart }) => (
                        <Box
                            sx={{
                                display:"flex",
                                flexDirection:"column",
                                overflow:"hidden",
                                cursor:"pointer"
                            }}
                            onMouseEnter={onMouseEnter}
                            onMouseLeave={onMouseLeave}
                            onTouchStart={onTouchStart}
                        >
                            <Text
                                sx={props.explainer ? {display: "flex", alignItems: "center"} : {}}
                                noBreak
                                size={"fs13"} >
                                {t("import_shipping_costs")}
                            </Text>
                            <Text
                                light
                                sx={{
                                pt:1,
                                }}
                            >
                                {t("upload_shipping_costs_file")}
                            </Text>
                        </Box>
                    )}
                </SHQuickAction>
                <Shbutton className={"always-visible"} onClick={() => handleSingleClick("verzendkosten")} color="primary" variant="text" startIcon={<FileUploadIcon/>} disabled={isUploading}>
                    {t("upload")}
                </Shbutton>
            </Box>
            </Box>

          </Box>
        </Box>
      </Box>

      {/* Product Import Settings Modal */}
      <MDynamicValues
        open={productImportModalOpen}
        handleClose={() => setProductImportModalOpen(false)}
        title={t("product_import_settings")}
        labelWidth={120}
        fields={[
          {
            name: "weighttype",
            label: t("weight_type"),
            type: "list",
            defaultValue: "g",
            options: [
              { id: "g", name: "g" },
              { id: "kg", name: "kg" }
            ]
          },
          {
            name: "dimensionstype",
            label: t("dimensions_type"),
            type: "list",
            defaultValue: "mm",
            options: [
              { id: "mm", name: "mm" },
              { id: "cm", name: "cm" }
            ]
          },
          {
            name: "onlyupdate",
            label: t("only_update"),
            type: "boolean",
            defaultValue: false
          },
          {
            name: "updatebarcodes",
            label: t("update_barcodes"),
            type: "boolean",
            defaultValue: false
          },
          {
            name: "setinactive",
            label: t("set_products_inactive"),
            type: "boolean",
            defaultValue: false
          }
        ]}
        onConfirm={handleProductImportConfirm}
      />

      {/* Shipping Costs Import Settings Modal */}
      <MDynamicValues
        open={shippingCostsModalOpen}
        handleClose={() => setShippingCostsModalOpen(false)}
        title={t("shipping_costs_import_settings")}
        subtitle={t("shipping_costs_import_settings_subtitle")}
        labelWidth={120}
        fields={[
          {
            name: "shipperType",
            label: t("shipper"),
            type: "list",
            required: true,
            defaultValue: "postnl",
            options: shippingCostShipperOptions
          }
        ]}
        onConfirm={handleShippingCostsImportConfirm}
      />

      {/* Naheffingen Import Settings Modal */}
      <MDynamicValues
        open={adjustmentChargesModalOpen}
        handleClose={() => setAdjustmentChargesModalOpen(false)}
        title={t("adjustment_charges_import_settings")}
        subtitle={t("adjustment_charges_import_settings_subtitle")}
        labelWidth={120}
        maxWidth={860}
        fields={adjustmentChargeFields}
        onSave={handleAdjustmentChargesOptionsSave}
        onConfirm={handleAdjustmentChargesImportConfirm}
      />
    </Box>
  );
};

export default SetupPanelImport;